import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSupportContext } from '../context/SupportContext';
import { useReportData, VALUE_SOURCE } from '../context/ReportDataContext';
import axios from 'axios';
import {
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Target,
  Shield,
  Zap,
  CreditCard,
  AlertCircle,
  ChevronRight,
  Plus,
  RefreshCw,
  Bug,
  Activity,
  Eye,
  EyeOff,
  Database,
  User,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import UnfinishedDisputeBanner from '../components/disputes/UnfinishedDisputeBanner';
import { disputeIntelligenceService } from '../services/disputeIntelligenceService';
import { metro2Service } from '../services/metro2Service';
import { buildResumeNavigation, clearStaleResumeLocalState, isMissingRoundError } from '../utils/disputeWizardResume';
import { useI18n } from '../context/I18nContext';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Score Ring Component
const ScoreRing = ({ score, maxScore = 850, size = 120, label = "Credit Score", color }) => {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / maxScore) * circumference;
  const strokeDashoffset = circumference - progress;

  const getScoreColor = () => {
    if (color) return color;
    if (score >= 750) return '#10B981';
    if (score >= 670) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth="8"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getScoreColor()}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-stone-900">{score || '—'}</span>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
    </div>
  );
};

// Bureau Score Card Component
const BureauScoreCard = ({ bureau, data, onViewDetails }) => {
  const bureauColors = {
    equifax: '#E11D48',
    experian: '#2563EB',
    transunion: '#7C3AED'
  };

  const bureauNames = {
    equifax: 'Equifax',
    experian: 'Experian',
    transunion: 'TransUnion'
  };

  // Check if we have explicit score data
  const hasScore = data && data.score !== null && data.score !== undefined;
  const scoreSource = data?.score_source || 'unknown';
  const isExplicitScore = scoreSource === 'explicit';

  if (!hasScore) {
    return (
      <div className="elite-card p-4 opacity-60" data-testid={`bureau-card-${bureau}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-700">{bureauNames[bureau]}</h4>
          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">No Data</span>
        </div>
        <div className="text-center py-4">
          <p className="text-sm text-slate-400">Score not available in report</p>
          <p className="text-xs text-slate-300 mt-1">Source: {scoreSource}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="elite-card p-4 hover:shadow-lg transition-all cursor-pointer border-t-4"
      style={{ borderTopColor: bureauColors[bureau] }}
      data-testid={`bureau-card-${bureau}`}
      onClick={onViewDetails}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-stone-900">{bureauNames[bureau]}</h4>
        <div className="flex items-center gap-2">
          {isExplicitScore && (
            <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">
              Verified
            </span>
          )}
          {data.possible_gain > 0 && (
            <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
              +{data.possible_gain} pts
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <ScoreRing 
          score={data.score} 
          size={80} 
          label="" 
          color={bureauColors[bureau]}
        />
        <div className="flex-1 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Negative</span>
            <span className="font-semibold text-red-600">{data.negative_count || 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Positive</span>
            <span className="font-semibold text-emerald-600">{data.positive_count || 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Inquiries</span>
            <span className="font-semibold text-slate-700">{data.inquiries_count || 0}</span>
          </div>
        </div>
      </div>

      {data.ai_insights && data.ai_insights.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-600 flex items-start gap-1">
            <Sparkles size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
            {data.ai_insights[0]}
          </p>
        </div>
      )}
    </div>
  );
};

// Debug Panel Component - Enhanced with full state visibility
const DebugPanel = ({ debug, userData, show, onToggle }) => {
  if (!show) {
    return (
      <button 
        onClick={onToggle}
        className="fixed bottom-4 right-4 p-2 bg-stone-800 text-white rounded-lg shadow-lg hover:bg-slate-700 transition-colors z-50"
        data-testid="debug-toggle"
        title="Show Debug Panel"
      >
        <Bug size={20} />
      </button>
    );
  }

  const isOldReport = !debug || (debug?.schema_version !== 'v2');
  const isDemoData = debug?.is_demo_data || debug?.is_mock_data;

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-stone-900 text-white rounded-xl shadow-2xl p-4 z-50 max-h-[80vh] overflow-y-auto" data-testid="debug-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold flex items-center gap-2">
          <Bug size={16} />
          Debug Info
        </h4>
        <button onClick={onToggle} className="text-slate-400 hover:text-white">
          <EyeOff size={16} />
        </button>
      </div>
      
      {/* Data Type Warning */}
      {isDemoData && (
        <div className="mb-3 p-2 rounded-lg bg-orange-900/50 border border-orange-600">
          <p className="text-xs text-orange-200 font-semibold">
            DEMO/SIMULATED DATA - Not real credit information
          </p>
        </div>
      )}
      
      {/* User & Session Info */}
      <div className="mb-3 pb-3 border-b border-slate-700">
        <p className="text-xs text-slate-400 mb-2 font-semibold">Session State</p>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-400">User ID:</span>
            <span className="text-slate-200 font-mono text-[10px]">{userData?.user_id || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Email:</span>
            <span className="text-slate-200">{userData?.email || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Report ID:</span>
            <span className="text-slate-200 font-mono text-[10px]">{userData?.report_id || 'None'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Dashboard State:</span>
            <span className={userData?.state === 'post_import' ? 'text-emerald-400' : 'text-amber-400'}>
              {userData?.state || 'unknown'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Last Updated:</span>
            <span className="text-slate-200 text-[10px]">{userData?.last_updated || 'N/A'}</span>
          </div>
        </div>
      </div>
      
      {/* Report Schema Info */}
      <div className="mb-3 pb-3 border-b border-slate-700">
        <p className="text-xs text-slate-400 mb-2 font-semibold">Report Schema</p>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-400">Schema Version:</span>
            <span className={isOldReport ? 'text-red-400' : 'text-emerald-400'}>
              {debug?.schema_version || 'v1 (OLD)'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Data Type:</span>
            <span className={isDemoData ? 'text-orange-400' : 'text-emerald-400'}>
              {isDemoData ? 'DEMO/SIMULATED' : 'Real Data'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Import Source:</span>
            <span className="text-slate-200">{debug?.import_source || 'unknown'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Has Bureau Data:</span>
            <span className={debug?.has_bureau_data ? 'text-emerald-400' : 'text-red-400'}>
              {debug?.has_bureau_data ? '✓ Yes' : '✗ No'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Bureaus Available:</span>
            <span className="text-slate-200">{(debug?.bureaus_found || []).join(', ') || 'None'}</span>
          </div>
        </div>
      </div>
      
      {/* Parsing & AI Status */}
      <div className="mb-3 pb-3 border-b border-slate-700">
        <p className="text-xs text-slate-400 mb-2 font-semibold">Processing Status</p>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-400">Uploaded File:</span>
            <span className="text-slate-200 text-[10px] truncate max-w-[150px]">
              {debug?.uploaded_filename || 'N/A'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Parsing Success:</span>
            <span className={debug?.parsing_failed ? 'text-red-400' : debug?.parsing_success ? 'text-emerald-400' : 'text-amber-400'}>
              {debug?.parsing_failed ? '✗ Failed' : debug?.parsing_success ? '✓ Yes' : '○ Unknown'}
            </span>
          </div>
          {debug?.parsing_error && (
            <div className="flex justify-between">
              <span className="text-slate-400">Error:</span>
              <span className="text-red-400 text-[10px]">{debug.parsing_error}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-400">Score Source:</span>
            <span className={debug?.score_source === 'extracted' ? 'text-emerald-400' : 'text-amber-400'}>
              {debug?.score_source || 'unknown'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">AI Analysis:</span>
            <span className={debug?.ai_analysis_success ? 'text-emerald-400' : 'text-amber-400'}>
              {debug?.ai_analysis_success ? '✓ Complete' : '○ Not Run'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Custom Rules Applied:</span>
            <span className="text-slate-200">{debug?.custom_rules_applied || 0}</span>
          </div>
        </div>
      </div>
      
      {/* Extraction Stats - NEW */}
      <div className="mb-3 pb-3 border-b border-slate-700">
        <p className="text-xs text-slate-400 mb-2 font-semibold">Extraction Statistics</p>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-400">Provider Detected:</span>
            <span className="text-slate-200">{debug?.provider_detected || 'unknown'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Parsing Method:</span>
            <span className="text-slate-200">{debug?.parsing_method || 'unknown'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Raw Text Length:</span>
            <span className="text-slate-200">{debug?.raw_text_length?.toLocaleString() || 0} chars</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Sections Found:</span>
            <span className="text-slate-200 text-[10px]">{(debug?.sections_found || []).join(', ') || 'None'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Tradelines Extracted:</span>
            <span className="text-emerald-400 font-semibold">{debug?.tradelines_extracted || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Inquiries Extracted:</span>
            <span className="text-slate-200">{debug?.inquiries_extracted || 0}</span>
          </div>
        </div>
      </div>
      
      {/* Value Sources - NEW */}
      {debug?.value_sources && (
        <div className="mb-3 pb-3 border-b border-slate-700">
          <p className="text-xs text-slate-400 mb-2 font-semibold">Value Sources (Per Bureau)</p>
          {Object.entries(debug.value_sources).map(([bureau, sources]) => (
            <div key={bureau} className="mb-2">
              <p className="text-xs text-slate-300 font-medium capitalize">{bureau}:</p>
              <div className="ml-2 space-y-0.5 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-400">Score:</span>
                  <span className={sources?.score === 'explicit' ? 'text-emerald-400' : sources?.score === 'derived' ? 'text-amber-400' : 'text-red-400'}>
                    {sources?.score || 'no_data'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Accounts:</span>
                  <span className={sources?.total_accounts === 'explicit' ? 'text-emerald-400' : sources?.total_accounts === 'derived' ? 'text-amber-400' : 'text-red-400'}>
                    {sources?.total_accounts || 'no_data'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Validation Warnings */}
      {debug?.validation_warnings?.length > 0 && (
        <div className="mb-3 p-2 rounded-lg bg-red-900/50 border border-red-600">
          <p className="text-xs text-red-200 font-semibold mb-1">Validation Warnings:</p>
          {debug.validation_warnings.map((w, i) => (
            <p key={i} className="text-[10px] text-red-300">{w}</p>
          ))}
        </div>
      )}
      
      {/* Pending Review Alert */}
      {userData?.pending_review_count > 0 && (
        <div className="mb-3 p-2 rounded-lg bg-amber-900/50 border border-amber-600">
          <p className="text-xs text-amber-200">
            <strong>{userData.pending_review_count} items</strong> pending admin review.
            Some accounts may not appear until reviewed.
          </p>
        </div>
      )}
      
      {/* Data Source Indicator */}
      <div className="p-2 rounded-lg bg-stone-800">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            isDemoData ? 'bg-orange-500' : 
            debug?.live_data ? 'bg-emerald-500' : 'bg-amber-500'
          } animate-pulse`}></div>
          <span className="text-xs">
            {isDemoData ? 'DEMO DATA (Not real)' :
             debug?.live_data ? 'Real parsed data from MongoDB' : 
             'Fallback/incomplete data'}
          </span>
        </div>
      </div>
      
      {/* Old Report Warning */}
      {isOldReport && !isDemoData && userData?.state === 'post_import' && (
        <div className="mt-3 p-2 rounded-lg bg-amber-900/50 border border-amber-600">
          <p className="text-xs text-amber-200">
            <strong>Old Report Detected:</strong> Re-import your report to see per-bureau scores and enhanced AI analysis.
          </p>
        </div>
      )}
    </div>
  );
};

// Pre-Import Dashboard State - Phase 9.2 Context-Aware
const PreImportDashboard = ({ data, onImportClick, supportContext }) => {
  const isViewingAsSupport = supportContext?.isInSupportMode && supportContext?.context?.effective_user_id;
  const effectiveClientName = isViewingAsSupport 
    ? (supportContext?.context?.effective_user_name || 'Customer')
    : null;

  const workflowItems = [
    { label: 'Complete Profile', done: data?.workflow_checklist?.profile_complete, icon: CheckCircle },
    { label: 'Import Credit Report', done: data?.workflow_checklist?.report_imported, icon: FileText },
    { label: 'Start First Dispute', done: data?.workflow_checklist?.first_dispute, icon: Shield },
    { label: 'Enable Monitoring', done: data?.workflow_checklist?.monitoring, icon: Target },
    { label: 'Funding Preparation', done: data?.workflow_checklist?.funding_prep, icon: TrendingUp }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pre-import-dashboard">
      {/* Welcome Card - Phase 9.2 Context-Aware */}
      <div className="elite-card elite-card-highlight p-6">
        <div className="flex items-start justify-between">
          <div>
            {isViewingAsSupport ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Eye size={18} className="text-emerald-600" />
                  <span className="text-sm text-emerald-600 font-medium">Support View</span>
                </div>
                <h2 className="text-2xl font-bold text-stone-900">
                  Self-Serve Customer: {effectiveClientName}
                </h2>
                <p className="text-slate-500 mt-2 max-w-lg">
                  This customer has not yet imported a credit report. Their AI-powered credit optimization journey will begin once they upload their report.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-stone-900">
                  Welcome to FundElite AI{data?.user?.name ? `, ${data.user.name.split(' ')[0]}` : ''}
                </h2>
                <p className="text-slate-500 mt-2 max-w-lg">
                  Your AI-powered credit optimization journey starts here. Import your credit report to unlock personalized insights, dispute recommendations, and funding readiness analysis.
                </p>
              </>
            )}
          </div>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-emerald-700">
              {isViewingAsSupport ? 'Awaiting Import' : 'Getting Started'}
            </span>
          </div>
        </div>

        {!isViewingAsSupport && (
          <Button 
            onClick={onImportClick}
            className="btn-primary mt-6"
            data-testid="import-report-cta"
          >
            <FileText size={18} className="mr-2" />
            Import Your Credit Report
            <ArrowRight size={18} className="ml-2" />
          </Button>
        )}
        {isViewingAsSupport && (
          <div className="mt-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <User size={16} className="text-slate-500" />
              <span>Customer needs to import their credit report to proceed.</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Funding Readiness Starter */}
        <div className="elite-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <TrendingUp size={20} className="text-slate-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Funding Readiness</p>
              <p className="text-2xl font-bold text-stone-900">{data?.funding_readiness_score || 0}%</p>
            </div>
          </div>
          <p className="text-sm text-slate-500">Import your report to calculate your funding readiness score</p>
        </div>

        {/* Active Disputes */}
        <div className="elite-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Shield size={20} className="text-slate-500" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Disputes</p>
              <p className="text-2xl font-bold text-stone-900">{data?.active_disputes || 0}</p>
            </div>
          </div>
          <p className="text-sm text-slate-500">No active disputes yet</p>
        </div>

        {/* Knowledge Base */}
        <div className="elite-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Sparkles size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Academy</p>
              <p className="text-lg font-semibold text-stone-900">Learn Credit</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full mt-2">
            Start Learning
          </Button>
        </div>
      </div>

      {/* Workflow Checklist - Phase 9.2 Context-Aware */}
      <div className="elite-card p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4">
          {isViewingAsSupport ? `${effectiveClientName}'s Credit Journey` : 'Your Credit Journey'}
        </h3>
        <div className="space-y-3">
          {workflowItems.map((item, index) => (
            <div 
              key={index}
              className={`flex items-center gap-3 p-3 rounded-lg ${item.done ? 'bg-emerald-50' : 'bg-slate-50'}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.done ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                {item.done ? (
                  <CheckCircle size={16} className="text-white" />
                ) : (
                  <span className="text-sm font-medium text-slate-500">{index + 1}</span>
                )}
              </div>
              <span className={`font-medium ${item.done ? 'text-emerald-700' : 'text-slate-600'}`}>
                {item.label}
              </span>
              {!item.done && index === 1 && !isViewingAsSupport && (
                <ChevronRight size={16} className="ml-auto text-slate-400" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* AI Helper Card - Phase 9.2 Context-Aware */}
      <div className="elite-card p-6 bg-gradient-to-r from-amber-50 to-emerald-50 border-amber-100">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles size={24} className="text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-stone-900">AI Credit Coach</h3>
            <p className="text-slate-600 mt-1">
              {isViewingAsSupport 
                ? `Once ${effectiveClientName} imports their report, AI will identify negative items, utilization issues, and recommend next best actions.`
                : (data?.ai_message || "Import your report to let AI identify negative items, utilization issues, and next best actions.")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Post-Import Dashboard State with Bureau Support - Phase 9.2 Context-Aware
const PostImportDashboard = ({ data, onRefresh, supportContext }) => {
  const navigate = useNavigate();
  // showDebug state removed - debug panels not shown to users

  // Phase 9.2: Support context awareness
  const isViewingAsSupport = supportContext?.isInSupportMode && supportContext?.context?.effective_user_id;
  const effectiveClientName = isViewingAsSupport 
    ? (supportContext?.context?.effective_user_name || 'Customer')
    : null;

  const bureauData = data?.bureau_data || {};
  const hasBureauData = Object.keys(bureauData).some(k => bureauData[k]?.score);
  const isOldReport = data?.debug?.schema_version !== 'v2';
  const isDemoData = data?.debug?.is_mock_data || data?.report_id?.startsWith('demo_');
  const hasNoRealData = !data?.metrics?.total_accounts && !hasBureauData;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="post-import-dashboard">
      {/* Header with Refresh - Phase 9.2 Context-Aware */}
      <div className="flex items-center justify-between">
        <div>
          {isViewingAsSupport ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Eye size={18} className="text-emerald-600" />
                <span className="text-sm text-emerald-600 font-medium">Support View</span>
              </div>
              <h1 className="text-2xl font-bold text-stone-900">Self-Serve Customer: {effectiveClientName}</h1>
            </>
          ) : (
            <h1 className="text-2xl font-bold text-stone-900">Credit Dashboard</h1>
          )}
          <p className="text-sm text-slate-500">
            Last imported: {data?.last_updated ? new Date(data.last_updated).toLocaleString() : 'Just now'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-dashboard">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Demo Data Warning Banner - Phase 9.2 Context-Aware */}
      {isDemoData && (
        <div className="elite-card p-4 bg-orange-50 border-orange-300 border-2" data-testid="demo-data-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-orange-600" size={24} />
              <div>
                <p className="font-semibold text-orange-800">Demo/Simulated Data</p>
                <p className="text-sm text-orange-700">
                  {isViewingAsSupport 
                    ? `This dashboard shows SIMULATED data for ${effectiveClientName}. The customer needs to upload their actual credit report PDF.`
                    : 'This dashboard shows SIMULATED data for demonstration. Upload your actual credit report PDF to see real data.'}
                </p>
              </div>
            </div>
            {!isViewingAsSupport && (
              <Button 
                onClick={() => navigate('/credit-report/import')}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                Upload Real Report
              </Button>
            )}
          </div>
        </div>
      )}

      {/* No Data Found Warning - Phase 9.2 Context-Aware */}
      {hasNoRealData && !isDemoData && (
        <div className="elite-card p-4 bg-red-50 border-red-300 border-2" data-testid="no-data-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-red-600" size={24} />
              <div>
                <p className="font-semibold text-red-800">No Credit Data Extracted</p>
                <p className="text-sm text-red-700">
                  {isViewingAsSupport 
                    ? `We couldn't extract credit data from ${effectiveClientName}'s uploaded file. They need to upload a valid credit report PDF.`
                    : "We couldn't extract credit data from your uploaded file. Please upload a valid credit report PDF."}
                </p>
              </div>
            </div>
            {!isViewingAsSupport && (
              <Button 
                onClick={() => navigate('/credit-report/import')}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Try Again
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Old Report Upgrade Banner - Phase 9.2 Context-Aware */}
      {isOldReport && !isDemoData && !hasNoRealData && (
        <div className="elite-card p-4 bg-amber-50 border-amber-200" data-testid="old-report-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-amber-600" size={24} />
              <div>
                <p className="font-semibold text-amber-800">Legacy Report Detected</p>
                <p className="text-sm text-amber-700">
                  {isViewingAsSupport 
                    ? `${effectiveClientName}'s report was imported before the bureau-specific update. They should re-import for per-bureau scores and enhanced AI analysis.`
                    : 'Your report was imported before the bureau-specific update. Re-import to see per-bureau scores and enhanced AI analysis.'}
                </p>
              </div>
            </div>
            {!isViewingAsSupport && (
              <Button 
                onClick={() => navigate('/credit-report/import')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <RefreshCw size={16} className="mr-2" />
                Re-import Report
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Bureau Score Cards - Side by Side */}
      {hasBureauData ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BureauScoreCard 
            bureau="equifax" 
            data={bureauData.equifax}
            onViewDetails={() => navigate('/credit-report')}
          />
          <BureauScoreCard 
            bureau="experian" 
            data={bureauData.experian}
            onViewDetails={() => navigate('/credit-report')}
          />
          <BureauScoreCard 
            bureau="transunion" 
            data={bureauData.transunion}
            onViewDetails={() => navigate('/credit-report')}
          />
        </div>
      ) : (
        /* Fallback Single Score View */
        <div className="elite-card p-6">
          <div className="flex items-center gap-6">
            <ScoreRing score={data?.score_data?.current_score || 0} size={140} />
            <div>
              <h2 className="text-lg font-semibold text-stone-900">Combined Score</h2>
              <div className="flex items-center gap-2 text-emerald-600 mt-1">
                <TrendingUp size={16} />
                <span className="font-semibold">+{data?.score_data?.possible_gain || 0} pts potential</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Potential Score: {data?.score_data?.potential_score || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Combined Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="elite-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <CreditCard size={18} className="text-slate-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900">{data?.metrics?.total_accounts || 0}</p>
              <p className="text-xs text-slate-500">Total Accounts</p>
            </div>
          </div>
        </div>

        <div className="elite-card p-4 border-l-4 border-l-red-400">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
              <AlertTriangle size={18} className="text-red-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900">{data?.metrics?.negative_count || 0}</p>
              <p className="text-xs text-slate-500">Negative Items</p>
            </div>
          </div>
        </div>

        <div className="elite-card p-4 border-l-4 border-l-emerald-400">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <CheckCircle size={18} className="text-emerald-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900">{data?.metrics?.positive_count || 0}</p>
              <p className="text-xs text-slate-500">Positive Items</p>
            </div>
          </div>
        </div>

        <div className="elite-card p-4 border-l-4 border-l-amber-400">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Activity size={18} className="text-amber-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900">{data?.metrics?.collections_count || 0}</p>
              <p className="text-xs text-slate-500">Collections</p>
            </div>
          </div>
        </div>

        <div className="elite-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Target size={18} className="text-amber-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-900">{data?.metrics?.disputable_items || 0}</p>
              <p className="text-xs text-slate-500">Disputable</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Summary */}
      {data?.ai_summary && (
        <div className="elite-card p-5 bg-gradient-to-r from-amber-50 to-slate-50 border-amber-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                AI Summary
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  {data?.ai_analysis_complete ? 'GPT-4o' : 'Basic'}
                </span>
              </h3>
              <p className="text-sm text-slate-600 mt-1">{data.ai_summary}</p>
            </div>
          </div>
        </div>
      )}

      {/* AI Alerts */}
      {data?.ai_alerts && data.ai_alerts.length > 0 && (
        <div className="elite-card p-5">
          <h3 className="font-semibold text-stone-900 flex items-center gap-2 mb-3">
            <AlertCircle size={18} className="text-amber-500" />
            AI Analysis Alerts
          </h3>
          <ul className="space-y-2">
            {data.ai_alerts.map((alert, index) => (
              <li key={index} className="text-sm text-slate-600 flex items-start gap-2 p-2 bg-slate-50 rounded-lg">
                <Sparkles size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                {alert}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next Actions */}
      <div className="elite-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">Recommended Next Actions</h3>
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">AI Powered</span>
        </div>
        <div className="space-y-3">
          {(data?.next_actions || []).map((action, index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
              onClick={() => navigate('/disputes')}
              data-testid={`action-${index}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  action.impact === 'HIGH IMPACT' || action.impact === 'HIGH' ? 'bg-red-100' :
                  action.impact === 'UTILIZATION ISSUE' || action.impact === 'MEDIUM' ? 'bg-amber-100' : 'bg-emerald-100'
                }`}>
                  <Zap size={20} className={
                    action.impact === 'HIGH IMPACT' || action.impact === 'HIGH' ? 'text-red-500' :
                    action.impact === 'UTILIZATION ISSUE' || action.impact === 'MEDIUM' ? 'text-amber-500' : 'text-emerald-500'
                  } />
                </div>
                <div>
                  <p className="font-medium text-stone-900">{action.title}</p>
                  <p className="text-sm text-slate-500">{action.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  action.impact === 'HIGH IMPACT' || action.impact === 'HIGH' ? 'bg-red-100 text-red-700' :
                  action.impact === 'UTILIZATION ISSUE' || action.impact === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {action.impact}
                </span>
                <span className="text-sm font-medium text-emerald-600">{action.points}</span>
                <ChevronRight size={18} className="text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Funding Readiness */}
        <div className="elite-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-900">Funding Readiness</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/funding')}>
              View Details
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-500">Readiness Score</span>
                <span className="font-semibold text-stone-900">{data?.funding_readiness?.score || 0}%</span>
              </div>
              <Progress value={data?.funding_readiness?.score || 0} className="h-2" />
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              data?.funding_readiness?.status === 'ready' ? 'bg-emerald-50 text-emerald-700' :
              data?.funding_readiness?.status === 'needs_work' ? 'bg-amber-50 text-amber-700' :
              'bg-red-50 text-red-700'
            }`}>
              {data?.funding_readiness?.status === 'ready' ? 'Ready' :
               data?.funding_readiness?.status === 'needs_work' ? 'Needs Work' : 'Not Ready'}
            </div>
          </div>
          {data?.funding_readiness?.blockers && data.funding_readiness.blockers.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-1">Blockers:</p>
              <ul className="text-xs text-red-600 space-y-1">
                {data.funding_readiness.blockers.slice(0, 2).map((blocker, i) => (
                  <li key={i}>• {blocker}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Active Disputes */}
        <div className="elite-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-900">Active Disputes</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/disputes')}>
              Manage Disputes
            </Button>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-stone-900">{data?.active_disputes || 0}</p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
            <Button 
              onClick={() => navigate('/disputes')}
              className="btn-secondary flex-1"
            >
              <Plus size={16} className="mr-2" />
              New Dispute
            </Button>
          </div>
        </div>
      </div>

      {/* Debug Panel removed - not shown to users */}
    </div>
  );
};

// Main Dashboard Component - Phase 9.2 Support Context Integration
const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, getAuthHeaders } = useAuth();
  const { refreshData } = useReportData();
  const { supportContext, isInSupportMode } = useSupportContext();
  const { t } = useI18n();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(true);
  const [resuming, setResuming] = useState(false);

  // Phase 9.2: Package support context for child components
  const supportContextData = {
    isInSupportMode,
    context: supportContext,
  };

  const fetchDashboard = useCallback(async () => {
    console.log('[Dashboard] fetchDashboard called, token:', token ? 'present' : 'missing');
    
    if (!token) {
      console.log('[Dashboard] No token, skipping fetch');
      setLoading(false);
      return;
    }
    
    try {
      console.log('[Dashboard] Fetching from API...');
      const response = await axios.get(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      console.log('[Dashboard] Response state:', response.data?.state);
      setDashboardData(response.data);
    } catch (error) {
      console.error('[Dashboard] Fetch error:', error.response?.status, error.message);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (!token || isInSupportMode) {
      setResumeLoading(false);
      return;
    }
    disputeIntelligenceService
      .getResumeCandidate(token)
      .then((res) => setResumeCandidate(res?.has_resume ? res.candidate : null))
      .catch(() => setResumeCandidate(null))
      .finally(() => setResumeLoading(false));
  }, [token, isInSupportMode]);

  const resumeRefreshAttemptedRef = React.useRef(false);

  const refreshResumeCandidateOnce = useCallback(async () => {
    if (!token || isInSupportMode || resumeRefreshAttemptedRef.current) return;
    resumeRefreshAttemptedRef.current = true;
    try {
      const res = await disputeIntelligenceService.getResumeCandidate(token);
      setResumeCandidate(res?.has_resume ? res.candidate : null);
    } catch {
      setResumeCandidate(null);
    }
  }, [token, isInSupportMode]);

  const handleResumeDispute = async () => {
    if (!resumeCandidate?.round_id || !token) return;
    const staleRoundId = resumeCandidate.round_id;
    setResuming(true);
    try {
      const detail = await metro2Service.getRound(staleRoundId, token);
      const navigation = buildResumeNavigation(detail, resumeCandidate);
      navigate(navigation.path, { state: navigation.state });
    } catch (error) {
      if (isMissingRoundError(error)) {
        clearStaleResumeLocalState(staleRoundId);
        setResumeCandidate(null);
        await refreshResumeCandidateOnce();
        toast.error(t('disputeWizardLanding.resumeFailed'));
      } else {
        toast.error(t('disputeWizardLanding.resumeFailed'));
      }
    } finally {
      setResuming(false);
    }
  };

  // Auto-refresh when returning from import page
  useEffect(() => {
    if (location.state?.refresh) {
      // Force refresh the report data context to clear any cached stale data
      refreshData();
      fetchDashboard();
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, refreshData]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchDashboard();
    toast.success('Dashboard refreshed');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const handleImportClick = () => {
    navigate('/credit-report/import');
  };

  return (
    <div data-testid="dashboard">
      {!isInSupportMode && (
        <UnfinishedDisputeBanner
          candidate={resumeCandidate}
          loading={resumeLoading}
          resuming={resuming}
          onResume={handleResumeDispute}
          onDismiss={() => setResumeCandidate(null)}
          t={t}
        />
      )}
      {dashboardData?.state === 'post_import' ? (
        <PostImportDashboard data={dashboardData} onRefresh={handleRefresh} supportContext={supportContextData} />
      ) : (
        <PreImportDashboard data={dashboardData} onImportClick={handleImportClick} supportContext={supportContextData} />
      )}
    </div>
  );
};

export default Dashboard;
